import { queryOptions } from "@tanstack/react-query";
import { apiClient } from "~/lib/client";
import { handleApiError, handleResponse } from "~/utils/response";
import { buildQueryString } from "~/utils/url";
import { EncounterResponse, EncountersQueryOptions, EncountersResponse } from "../types";
import { encountersKeys } from "./keys";
import { formatEncounterFullDateTime, formatFullDate, formatEncounterStatusTag, formatToMonthDayYear, formatEncounterFullTime } from "@workspace/lib/date";
import { EncounterAppointmentStatus } from "../../types";

export const encountersQueryOptions = (opts: EncountersQueryOptions) => {
    return queryOptions({
        queryKey: encountersKeys.encounters(opts),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/encounters${buildQueryString(opts)}`);
                const result = await handleResponse<EncountersResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                // console.log("encountersQueryOptions ~ queryFn", { query: buildQueryString(opts), result: result.data })

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            return {
                encounters: data.encounters?.map(encounter => ({
                    id: encounter.type === "appointment" ? encounter.appointment_id : encounter.type === "refill" ? encounter.is_batch_refill ? encounter.refill_batch_id : encounter.refill_id : encounter.type === "prescription" ? encounter.prescription_id : encounter.type === "consultation" ? encounter.encounter_id : encounter.encounter_id ?? undefined,
                    type: encounter.type,
                    encounterId: encounter.encounter_id ?? "",
                    title: encounter.type === "appointment" ? `${encounter.patient.name} Appointment` : encounter.type === "refill" ? `${encounter.patient.name} Refill Request` : encounter.type === "prescription" ? `${encounter.patient.name} Prescription` : encounter.type === "consultation" ? `${encounter.patient.name} Consultation` : `${encounter.patient.name}`,
                    description: encounter.type === "appointment" ? "Medical Appointment" : encounter.type === "refill" ? "Medication Refill Request" : encounter.type === "prescription" ? "Prescription" : encounter.type === "consultation" ? "Consultation" : "Encounter",
                    date: encounter.type === "appointment" ? formatEncounterFullDateTime(encounter.encounter_datetime) : formatFullDate(encounter?.encounter_datetime),
                    time: `${encounter.encounter_datetime ? formatEncounterFullTime(encounter.encounter_datetime) : "No Time Available"}`,
                    createdAt: formatToMonthDayYear(encounter?.encounter_datetime),
                    instanceCreated: encounter?.encounter_datetime,
                    is_consultation: encounter.type === "appointment" ? (encounter.is_consultation ?? false) : false,
                    medicalRefills: encounter.type === "refill"
                        ? (encounter.medications && Array.isArray(encounter.medications)
                            ? encounter.medications.map(med => ({
                                name: med.name,
                                dosage: med.dosage,
                                quantity: med.quantity,
                                duration: med.duration,
                                refills: med.refills
                            }))
                            : encounter.medication
                                ? (Array.isArray(encounter.medication)
                                    ? encounter.medication.map(med => ({
                                        name: med.name,
                                        dosage: med.dosage,
                                    }))
                                    : (encounter.medication && typeof encounter.medication === 'object'
                                        ? [{
                                            name: (encounter.medication as { name?: string })?.name ?? "",
                                            dosage: (encounter.medication as { dosage?: string })?.dosage ?? "",
                                        }]
                                        : []
                                    )
                                )
                                : []
                        )
                        : undefined,
                    imageUrls: encounter.type === "refill" ? encounter.urls?.images || [] : undefined,
                    fileUrls: encounter.type === "refill" ? encounter.urls?.files || [] : undefined,
                    tag: `${formatEncounterStatusTag({ date: encounter?.encounter_datetime ?? "", status: encounter.type === "appointment" ? encounter.status as EncounterAppointmentStatus : "scheduled", meetingInterval: encounter.time_interval_minutes }).tag}`,
                    status: encounter.type === "refill"
                        ? (encounter.status as string) // Preserve refill status (rejected, fulfilled, etc.)
                        : `${formatEncounterStatusTag({ date: encounter?.encounter_datetime ?? "", status: encounter.type === "appointment" ? encounter.status as EncounterAppointmentStatus : "scheduled", meetingInterval: encounter.time_interval_minutes }).status}`,
                    locationTime: "Local Time",
                    notes: encounter.notes,
                    patient: {
                        id: encounter.patient.user_id,
                        name: encounter.patient.name,
                        gender: encounter.patient.gender ?? "",
                        age: encounter.patient.age,
                        height: encounter.patient.height,
                        weight: encounter.patient.weight,
                        bmi: encounter.patient.bmi,
                        user_dosespot_id: encounter.patient.user_dosespot_id,
                        urgent_care_onboarded: encounter.patient.urgent_care_onboarded,
                        email: encounter.patient.email,
                        target_weight: encounter.patient.target_weight,
                        timeframe: encounter.patient.timeframe,
                        glp1_guide_accepted: encounter.patient.glp1_guide_accepted,
                        identity_verified: encounter.patient.identity_verified.verified,
                        medical_plan: {
                            program: encounter.patient.medical_plan?.program,
                            session_id: encounter.patient.medical_plan?.session_id,
                            medication: encounter.patient.medical_plan?.medication ?? {},
                            vitamin: encounter.patient.medical_plan?.vitamin ?? {},
                            consultation: encounter.patient.medical_plan?.consultation ?? {},
                            consultation_type: encounter.patient.medical_plan?.consultation_type ?? "",
                            success_url: encounter.patient.medical_plan?.success_url ?? "http://localhost:5174/consultations/confirmation",
                            cancel_url: encounter.patient.medical_plan?.cancel_url ?? "http://localhost:5174/consultations/error",
                            plan_id: encounter.patient.medical_plan?.plan_id ?? "6850f5f5e9152794ee4e69c0",
                            user_id: encounter.patient.medical_plan?.user_id ?? "6850f5cce9152794ee4e69af",
                            payment_status: encounter.patient.medical_plan?.payment_status ?? "paid",
                            supplementary_questionnaire: encounter.patient.medical_plan?.supplementary_questionnaire ?? {},
                            created_datetime: encounter.patient.medical_plan?.created_datetime ?? "",
                            created_at: encounter.patient.medical_plan?.created_at ?? ""
                        },
                        supplementary_health_assessment: encounter.patient?.supplementary_health_assessment ?? {},
                        health_profile: encounter.patient?.health_profile,
                    },
                    recommendedTasks: encounter.recommended_tasks || [],

                    session: {
                        diagnosis: encounter.session?.diagnosis ?? [],
                        session_id: encounter.session?.session_id,
                        reported_symptom: encounter.session?.reported_symptom || "",
                        final_questions: encounter.session?.final_questions ?? null,
                        intake_questions: encounter.session?.intake_questions ?? undefined,
                        red_flag_questions: encounter.session?.red_flag_questions ?? undefined,
                        reported_symptoms: encounter.session?.reported_symptoms ?? null,
                        icd10_code: encounter.session?.icd10_code,
                        symptoms: encounter.session?.symptoms ?? null,
                    },
                })),
                hasMore: !!(data?.total > (opts?.limit ?? 10)),
                totalCount: data.total,
            }
        },
    });
};

export const upcomingEncountersQueryOptions = (opts: EncountersQueryOptions) => {
    return queryOptions({
        queryKey: encountersKeys.upcomingEncounters(opts),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/encounters${buildQueryString(opts)}`);
                const result = await handleResponse<EncountersResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            if (!data.encounters || data.encounters.length === 0) {
                return { encounters: [], totalCount: data.total ?? 0 };
            }
            const mapped = data.encounters.map(encounter => ({
                id: encounter.type === "appointment" ? encounter.appointment_id : encounter.type === "refill" ? encounter.is_batch_refill ? encounter.refill_batch_id : encounter.refill_id : encounter.type === "prescription" ? encounter.prescription_id : encounter.type === "consultation" ? encounter.encounter_id : encounter.encounter_id ?? undefined,
                type: encounter.type,
                encounterId: encounter.encounter_id,
                title: encounter.type === "appointment" ? `${encounter.patient.name} Appointment` : encounter.type === "refill" ? `${encounter.patient.name} Refill Request` : encounter.type === "prescription" ? `${encounter.patient.name} Prescription` : encounter.type === "consultation" ? `${encounter.patient.name} Consultation` : `${encounter.patient.name}`,
                subtitle: encounter.type === "appointment" ? encounter.subtitle || "" : "",
                description: encounter.type === "appointment" ? "Medical Appointment" : encounter.type === "refill" ? "Medication Refill Request" : encounter.type === "prescription" ? "Prescription" : encounter.type === "consultation" ? "Consultation" : "Encounter",
                date: encounter.type === "appointment" ? formatEncounterFullDateTime(encounter.encounter_datetime) : formatFullDate(encounter?.encounter_datetime),
                time: `${encounter.encounter_datetime ? formatEncounterFullTime(encounter.encounter_datetime) : "No Time Available"}`,
                createdAt: formatToMonthDayYear(encounter?.encounter_datetime),
                instanceCreated: encounter?.encounter_datetime,
                medicalRefills: encounter.type === "refill"
                    ? (encounter.medications && Array.isArray(encounter.medications)
                        ? encounter.medications.map(med => ({
                            name: med.name,
                            dosage: med.dosage,
                            quantity: med.quantity,
                            duration: med.duration,
                            refills: med.refills
                        }))
                        : encounter.medication
                            ? (Array.isArray(encounter.medication)
                                ? encounter.medication.map(med => ({
                                    name: med.name,
                                    dosage: med.dosage,
                                }))
                                : (encounter.medication && typeof encounter.medication === 'object'
                                    ? [{
                                        name: (encounter.medication as { name?: string })?.name ?? "",
                                        dosage: (encounter.medication as { dosage?: string })?.dosage ?? "",
                                    }]
                                    : []
                                )
                            )
                            : []
                    )
                    : undefined,
                locationTime: "Local Time",
                is_consultation: encounter.type === "appointment" ? (encounter.is_consultation ?? false) : false,
                tag: `${formatEncounterStatusTag({ date: encounter?.encounter_datetime ?? "", status: encounter.type === "appointment" ? encounter.status as EncounterAppointmentStatus : "scheduled", meetingInterval: encounter.time_interval_minutes }).tag}`,
                status: encounter.type === "refill"
                    ? (encounter.status as string) // Preserve refill status (rejected, fulfilled, etc.)
                    : `${formatEncounterStatusTag({ date: encounter?.encounter_datetime ?? "", status: encounter.type === "appointment" ? encounter.status as EncounterAppointmentStatus : "scheduled", meetingInterval: encounter.time_interval_minutes }).status}`,
                session: encounter.session,
                notes: encounter.notes,
                patient: {
                    id: encounter.patient.user_id,
                    name: encounter.patient.name,
                    gender: encounter.patient.gender,
                    email: encounter.patient.email,
                    age: encounter.patient.age,
                    height: encounter.patient.height,
                    weight: encounter.patient.weight,
                    target_weight: encounter.patient.target_weight,
                    timeframe: encounter.patient.timeframe,
                    bmi: encounter.patient.bmi,
                    user_dosespot_id: encounter.patient.user_dosespot_id,
                    urgent_care_onboarded: encounter.patient.urgent_care_onboarded,
                    glp1_guide_accepted: encounter.patient.glp1_guide_accepted,
                    identity_verified: encounter.patient.identity_verified.verified,
                    medical_plan: {
                        program: encounter.patient.medical_plan?.program,
                        session_id: encounter.patient.medical_plan?.session_id,
                        medication: encounter.patient.medical_plan?.medication ?? {},
                        vitamin: encounter.patient.medical_plan?.vitamin ?? {},
                        consultation: encounter.patient.medical_plan?.consultation ?? {},
                        consultation_type: encounter.patient.medical_plan?.consultation_type ?? "",
                        success_url: encounter.patient.medical_plan?.success_url ?? "http://localhost:5174/consultations/confirmation",
                        cancel_url: encounter.patient.medical_plan?.cancel_url ?? "http://localhost:5174/consultations/error",
                        plan_id: encounter.patient.medical_plan?.plan_id ?? "6850f5f5e9152794ee4e69c0",
                        user_id: encounter.patient.medical_plan?.user_id ?? "6850f5cce9152794ee4e69af",
                        payment_status: encounter.patient.medical_plan?.payment_status ?? "paid",
                        supplementary_questionnaire: encounter.patient.medical_plan?.supplementary_questionnaire ?? {},
                        created_datetime: encounter.patient.medical_plan?.created_datetime ?? "",
                        created_at: encounter.patient.medical_plan?.created_at ?? ""
                    },
                    supplementary_health_assessment: encounter.patient?.supplementary_health_assessment ?? {},
                    health_profile: encounter.patient?.health_profile,
                },
                recommendedTasks: encounter.recommended_tasks || [],
            }));
            return { encounters: mapped, totalCount: data.total ?? 0 };
        },
    });
};

export const encounterByIdQueryOptions = (id: string, program: string) => {
    return queryOptions({
        queryKey: encountersKeys.encounterById(id),
        retry: false,
        queryFn: async () => {
            try {
                // const isMedicalPlan = program === "weight_loss";
                // const isLiveConsultation = name === "liveconsultation";
                // const isConsultation = name === "consultation";
                // const isAppointment = name === "appointment";
                // let encounterType = name;
                // if (isLiveConsultation) {
                //     encounterType = "appointment";
                // }
                // const shouldIncludeMedicalPlan = (isMedicalPlan && (!isAppointment || isLiveConsultation || isConsultation));
                const url = `/admin/encounters/${id}?program=${program ?? ""}`;
                const response = await apiClient(url);
                const result = await handleResponse<EncounterResponse>(response);

                if (!result.success) {
                    throw new Error(result.error);
                }

                return result.data;
            } catch (error) {
                handleApiError(error);
                throw error;
            }
        },
        select(data) {
            return {
                id: data.encounter.type === "appointment" ? data.encounter.appointment_id : data.encounter.type === "refill" ? data.encounter.is_batch_refill ? data.encounter.refill_batch_id : data.encounter.refill_id : data.encounter.type === "prescription" ? data.encounter.prescription_id : data.encounter.type === "consultation" ? data.encounter.encounter_id : data.encounter.encounter_id ?? undefined,
                type: data.encounter.type ?? "",
                encounterId: data.encounter.encounter_id ?? "",
                title: `${data.encounter.type === "appointment" ? `Appointment (${data.encounter.patient.name})` : data.encounter.type === "consultation" ? `Consultation (${data.encounter.patient.name})` : data.encounter.type === "prescription" ? `Prescription (${data.encounter.patient.name})` : `Medical Refill (${data.encounter.patient.name})`}`,
                description: `${data.encounter.type === "appointment" ? "Medical Consultation" : data.encounter.type === "consultation" ? "Consultation" : data.encounter.type === "prescription" ? "Prescription" : "Medication Refill Request"}`,
                date: data.encounter.type === "appointment" ? formatEncounterFullDateTime(data.encounter.encounter_datetime) : formatFullDate(data.encounter?.encounter_datetime),
                time: `${data.encounter.encounter_datetime ? formatEncounterFullTime(data.encounter.encounter_datetime) : "No Time Available"}`,
                createdAt: formatToMonthDayYear(data.encounter?.encounter_datetime),
                medicalRefills: data.encounter.type === "refill"
                    ? (data.encounter.medications && Array.isArray(data.encounter.medications)
                        ? data.encounter.medications.map(med => ({
                            name: med.name,
                            dosage: med.dosage,
                            quantity: med.quantity,
                            duration: med.duration,
                            refills: med.refills
                        }))
                        : data.encounter.medication && Array.isArray(data.encounter.medication)
                            ? data.encounter.medication.map(med => ({
                                name: med.name,
                                dosage: med.dosage,
                            }))
                            : []
                    )
                    : undefined,
                tag: `${formatEncounterStatusTag({ date: data.encounter?.encounter_datetime ?? "", status: data.encounter.type === "appointment" ? data.encounter.status as EncounterAppointmentStatus : "scheduled", meetingInterval: data.encounter.time_interval_minutes }).tag}`,
                status: `${formatEncounterStatusTag({ date: data.encounter?.encounter_datetime ?? "", status: data.encounter.type === "appointment" ? data.encounter.status as EncounterAppointmentStatus : "scheduled", meetingInterval: data.encounter.time_interval_minutes }).status}`,
                is_consultation: data.encounter.type === "appointment" ? (data.encounter.is_consultation ?? false) : false,
                locationTime: "Local Time",
                notes: data.encounter.notes,
                patient: {
                    id: data.encounter.patient.user_id,
                    name: data.encounter.patient.name,
                    gender: data.encounter.patient.gender ?? "",
                    age: data.encounter.patient.age,
                    height: data.encounter.patient.height,
                    weight: data.encounter.patient.weight,
                    target_weight: data.encounter.patient.target_weight,
                    timeframe: data.encounter.patient.timeframe,
                    bmi: data.encounter.patient.bmi,
                    user_dosespot_id: data.encounter.patient.user_dosespot_id,
                    email: data.encounter.patient.email,
                    glp1_guide_accepted: data.encounter.patient.glp1_guide_accepted,
                    identity_verified: data.encounter.patient.identity_verified.verified,
                    urgent_care_onboarded: data.encounter.patient.urgent_care_onboarded,
                    medical_plan: {
                        program: data.encounter.patient.medical_plan?.program,
                        session_id: data.encounter.patient.medical_plan?.session_id,
                        medication: data.encounter.patient.medical_plan?.medication ?? {},
                        vitamin: data.encounter.patient.medical_plan?.vitamin ?? {},
                        consultation: data.encounter.patient.medical_plan?.consultation ?? {},
                        consultation_type: data.encounter.patient.medical_plan?.consultation_type ?? "",
                        success_url: data.encounter.patient.medical_plan?.success_url ?? "http://localhost:5174/consultations/confirmation",
                        cancel_url: data.encounter.patient.medical_plan?.cancel_url ?? "http://localhost:5174/consultations/error",
                        plan_id: data.encounter.patient.medical_plan?.plan_id ?? "",
                        user_id: data.encounter.patient.medical_plan?.user_id ?? "",
                        payment_status: data.encounter.patient.medical_plan?.payment_status ?? "",
                        supplementary_questionnaire: data.encounter.patient.medical_plan?.supplementary_questionnaire ?? {},
                        created_datetime: data.encounter.patient.medical_plan?.created_datetime ?? "",
                        created_at: data.encounter.patient.medical_plan?.created_at ?? ""
                    }
                },
                recommendedTasks: data.encounter?.recommended_tasks || [],
                session: {
                    diagnosis: data.encounter.session?.diagnosis ?? [],
                    session_id: data.encounter.session?.session_id,
                    reported_symptom: data.encounter.session?.reported_symptom || "",
                    final_questions: data.encounter.session?.final_questions ?? null,
                    intake_questions: data.encounter.session?.intake_questions ?? undefined,
                    red_flag_questions: data.encounter.session?.red_flag_questions ?? undefined,
                    reported_symptoms: data.encounter.session?.reported_symptoms ?? null,
                    symptoms: data.encounter.session?.symptoms ?? null,
                },
                diagnosis: data.encounter.session?.diagnosis ?? [],
            }
        },
    });
};
